import { MapUtils } from '../../../utility/map-utils.js';
import { VersionRange } from '../../core/version-range.js';
import { cloneList, DataModel, DataModelBuilder, isSameData, isSameList, SerializationContext, SerializedData } from '../../dto/dto.js';
import { LocalizedString } from '../localized-string.js';

export class TicketReservationItem extends DataModel {
  static readonly TYPE_ID: string = '25b3bffe70f34e0697326b14c3c639da';
  static readonly SCHEMA_VERSION: number = 2;

  constructor(
    public readonly uniqueId: string,
    public readonly templateId: string,
    public readonly templateName: LocalizedString,
    public readonly quantity: number,
    // When a reservation for this ticket type is made, all the sibling tickets are also incremented in the pool.
    public readonly siblingTicketTemplateIds: string[],
  ) {
    super();
  }

  get $typeId() {
    return TicketReservationItem.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, TicketReservationItem.SCHEMA_VERSION);
  }

  static fromMap(context: SerializationContext, map: SerializedData): TicketReservationItem {
    return new TicketReservationItem(
        MapUtils.get<string>(map, 'uniqueId'),
        MapUtils.get<string>(map, 'templateId'),
        context.fromData<LocalizedString>(MapUtils.getRaw(map, 'templateName') as SerializedData),
        MapUtils.get<number>(map, 'quantity'),
        MapUtils.getList<string>(map, 'siblingTicketTemplateIds', (value) => value as string),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'templateId': this.templateId,
      'templateName': context.toData(this.templateName),
      'quantity': this.quantity,
      'siblingTicketTemplateIds': cloneList(this.siblingTicketTemplateIds),
    };
  }

  clone(): this {
    return new TicketReservationItem(
        this.uniqueId,
        this.templateId,
        this.templateName.clone(),
        this.quantity,
        cloneList(this.siblingTicketTemplateIds),
    ) as this;
  }

  isSame(other: TicketReservationItem): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
        other.templateId === this.templateId &&
        isSameData(other.templateName, this.templateName) &&
        isSameList(other.siblingTicketTemplateIds, this.siblingTicketTemplateIds) &&
        other.quantity === this.quantity;
  }
}

export class TicketReservationItemBuilder extends DataModelBuilder<TicketReservationItem> {
  build(context: SerializationContext, map: SerializedData): TicketReservationItem {
    return TicketReservationItem.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(TicketReservationItem.SCHEMA_VERSION)) {
      return map;
    }

    if(versionRange.isGreaterThan(TicketReservationItem.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version parameter `siblingTicketTemplateIds` of type `string[]` was introduced.
    if(version < 2) {
      map['siblingTicketTemplateIds'] = [];
      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
